import { DOCUMENT, Location } from "@angular/common";
import {
  Component,
  EventEmitter,
  HostListener,
  Inject,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { filter } from "rxjs/operators";
import {
  Contractor,
  SelectContractorRequest,
} from "src/app/core/models/contractor.model";
import { UserUpdate } from "src/app/core/models/user.model";
import { ContractorService } from "src/app/core/services/contractor.service";
import { EventService } from "src/app/core/services/event.service";
import { StorageService } from "src/app/core/services/storage.service";
import { ThemeService } from "src/app/core/services/theme.service";
import { UserService } from "src/app/core/services/user.service";
import { WhiteLabelService } from "src/app/core/services/whitelabel.service";
import { NotificationService } from "src/app/shared/isolutions-ui/feedback/notifications/notification.service";
import { environment } from "../../../environments/environment";
import { AuthService } from "../../core/services/auth.service";
import { LanguageService } from "../../core/services/language.service";

interface Language {
  text: string;
  flag: string;
  lang: string;
  value: number;
}

@Component({
  selector: "app-topbar",
  templateUrl: "./topbar.component.html",
  styleUrls: ["./topbar.component.scss"],
})
export class TopbarComponent implements OnInit {
  username: string | undefined;
  user: any;
  element: any;
  cookieValue: string | undefined;
  countryName: string | undefined;
  flagvalue: string | undefined;
  version: string = environment.version;
  openMobileMenu: boolean | undefined;
  pageTitle = "";
  canGoBack = false;
  searchQuery = "";
  unreadNotifications = 3;
  langExpanded = false;

  // Contractor selector
  showContractorSelector = false;
  contractors: Contractor[] = [];
  contractorName = "";
  selectedContractor: Contractor | null = null;

  notifications = [
    {
      icon: "bx-cart",
      color: "primary",
      title: "Novo pedido recebido",
      message: "Pedido #1234 aguardando aprovação",
      time: "Há 3 min",
    },
    {
      icon: "bx-user-plus",
      color: "success",
      title: "Novo usuário cadastrado",
      message: "João Silva criou uma conta",
      time: "Há 15 min",
    },
    {
      icon: "bx-error-circle",
      color: "danger",
      title: "Falha no processamento",
      message: "Erro ao processar pagamento #5678",
      time: "Há 1 hora",
    },
  ];

  /** Páginas disponíveis para busca — traduzidas dinamicamente */
  get searchPages(): any[] {
    return [
      {
        value: "/dashboard",
        label: this.translate.instant("MENU.DASHBOARD"),
        icon: "bx-bar-chart-alt-2",
        description: "Dashboard",
      },
      {
        value: "/equipments",
        label: this.translate.instant("MENU.EQUIPMENTS"),
        icon: "bx-devices",
        description: "Equipamentos",
      },
      {
        value: "/importation",
        label: this.translate.instant("MENU.IMPORTATION"),
        icon: "bx-import",
        description: "Importação CSV",
      },
      {
        value: "/settings",
        label: this.translate.instant("MENU.SETTINGS"),
        icon: "bx-cog",
        description: "Configurações",
      },
    ];
  }

  /** Mapeamento de rotas para chaves de tradução */
  private readonly routeTitleMap: Record<string, string> = {
    "/dashboard": "MENU.DASHBOARD",
    "/equipments": "MENU.EQUIPMENTS",
    "/equipment-map": "MENU.EQUIPMENT_MAP",
    "/importation": "MENU.IMPORTATION",
  };

  listLang: Language[] = [
    {
      text: "Português",
      flag: "assets/images/flags/br.png",
      lang: "pt",
      value: 1,
    },
    {
      text: "English",
      flag: "assets/images/flags/us.jpg",
      lang: "en",
      value: 2,
    },
    {
      text: "Español",
      flag: "assets/images/flags/spain.jpg",
      lang: "es",
      value: 3,
    },
  ];

  @Output() settingsButtonClicked = new EventEmitter();
  @Output() mobileMenuButtonClicked = new EventEmitter();

  @ViewChild("searchField") searchField: any;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private router: Router,
    private location: Location,
    private authService: AuthService,
    public languageService: LanguageService,
    public translate: TranslateService,
    public userService: UserService,
    public contractorService: ContractorService,
    public toastr: ToastrService,
    private notify: NotificationService,
    private eventService: EventService,
    private storageService: StorageService,
    public wl: WhiteLabelService,
    private themeService: ThemeService,
  ) {}

  ngOnInit() {
    this.openMobileMenu = false;
    this.element = document.documentElement;
    this.getUserName();
    this.initializeLanguage();
    this.initializeContractorSelector();
    this.updatePageTitle(this.router.url);

    // Escuta mudanças de rota para atualizar título
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe((event) => {
        this.updatePageTitle(event.urlAfterRedirects || event.url);
      });
  }

  /** Inicializa o seletor de contratante se o usuário já tem um vinculado */
  initializeContractorSelector(): void {
    const user = this.authService.getUser();
    if (user?.contractorId && user.contractorId > 0) {
      this.contractorName = user.contractorName || "";
      this.selectedContractor = {
        id: user.contractorId,
        name: user.contractorName || "",
        code: user.contractorCode || "",
        alias: "",
      };
      this.loadContractors();
      this.showContractorSelector = true;
    }
  }

  /** Carrega contratantes disponíveis */
  loadContractors(): void {
    this.contractorService.getAvailable().subscribe({
      next: (response: any) => {
        if (response.isSuccessful) {
          this.contractors = Array.isArray(response.data)
            ? response.data
            : [response.data];
        }
      },
    });
  }

  /** Troca o contratante ativo */
  onContractorChange(contractor: Contractor): void {
    if (!contractor) return;

    const request: SelectContractorRequest = {
      contractorId: contractor.id,
      contractorName: contractor.name,
      contractorCode: contractor.code,
    };

    // Persiste a troca no servidor e só recarrega após confirmação
    this.userService.selectContractor(request).subscribe({
      next: (response) => {
        if (response.isSuccessful) {
          // Atualiza localmente após sucesso no servidor
          this.authService.updateUserContractor(contractor.id, contractor.name);
          this.selectedContractor = contractor;
          this.contractorName = contractor.name;
          this.storageService.set("contractor", contractor, 30);

          // Recarrega a página para que as APIs usem o novo contratante
          window.location.reload();
        } else {
          this.notify.error(
            response.messageDescription || "Erro ao trocar contratante",
          );
        }
      },
      error: () => {
        this.notify.error("Erro ao trocar contratante");
      },
    });
  }

  /** Atualiza o título da página baseado na rota atual */
  private updatePageTitle(url: string): void {
    const path = "/" + url.split("/").filter(Boolean)[0];
    const titleKey = this.routeTitleMap[path];
    this.pageTitle = titleKey ? this.translate.instant(titleKey) : "";
    this.canGoBack = path !== "/dashboard";
  }

  /** Navega para a página anterior */
  goBack(): void {
    this.location.back();
  }

  /** Navega para a página selecionada e limpa o campo */
  onPageSelected(option: any): void {
    this.router.navigate([option.value]);
    // Limpa o campo após navegação
    setTimeout(() => {
      if (this.searchField) {
        this.searchField.clear();
      }
    }, 100);
  }

  /** Atalho Ctrl+Shift+A para focar na busca */
  @HostListener("document:keydown", ["$event"])
  onGlobalKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey && event.shiftKey && event.key === "A") {
      event.preventDefault();
      this.focusSearch();
    }
  }

  /** Foca no campo de busca */
  private focusSearch(): void {
    if (this.searchField?.inputEl?.nativeElement) {
      this.searchField.inputEl.nativeElement.focus();
    }
  }

  /** Toggle de tema (usado no dropdown do usuário) */
  get themeIcon(): string {
    return this.themeService.effectiveTheme === "dark" ? "bx-sun" : "bx-moon";
  }

  get themeLabel(): string {
    return this.themeService.effectiveTheme === "dark"
      ? "Tema claro"
      : "Tema escuro";
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  /**
   * Inicializa a linguagem baseado no usuário do localStorage
   */
  initializeLanguage() {
    // Pega a linguagem do usuário (que já vem do localStorage via getUserName)
    if (this.user?.language !== null && this.user?.language !== undefined) {
      const userLang = this.listLang.find(
        (x) => x.value === this.user.language,
      );
      if (userLang) {
        this.cookieValue = userLang.lang;
      }
    }

    // Se não encontrou linguagem do usuário, usa português como padrão
    if (!this.cookieValue) {
      this.cookieValue = "pt";
    }

    // Define os valores da flag e nome do país
    const val = this.listLang.filter((x) => x.lang === this.cookieValue);
    this.countryName = val.map((element) => element.text)[0];

    if (val.length === 0) {
      this.flagvalue = "assets/images/flags/br.png";
    } else {
      this.flagvalue = val.map((element) => element.flag)[0];
    }

    // Sempre aplica a linguagem
    this.languageService.setLanguage(this.cookieValue);
  }

  getUserName() {
    const user = this.authService.getUser();
    this.username = user?.name;
    this.user = user;
  }

  createUserUpdate(user: any): UserUpdate {
    const userUpdate = new UserUpdate();
    userUpdate.flActive = true;
    return userUpdate;
  }

  /**
   * Define a linguagem selecionada
   */
  setLanguage(text: string, lang: string, flag: string, value: number) {
    this.countryName = text;
    this.flagvalue = flag;
    this.cookieValue = lang;
    this.languageService.setLanguage(lang);
    this.saveUserLanguage(value);
  }

  /**
   * Salva a linguagem do usuário no banco de dados
   */
  saveUserLanguage(languageValue: number) {
    const userUpdate = new UserUpdate();
    userUpdate.flActive = true;
    userUpdate.language = languageValue;

    this.userService.updateUser(this.user.userId, userUpdate).subscribe({
      next: (response) => {
        if (response.isSuccessful) {
          this.user = response.data[0];
          this.authService.updateUserLanguage(this.user);
          this.user.language = languageValue;

          this.notify.success(
            this.translate.instant("TOAST.LANGUAGE_CHANGED_SUCCESS"),
          );

          // Recarrega a página para aplicar as traduções
          setTimeout(() => {
            window.location.reload();
          }, 500);
        } else {
          this.notify.error(response.messageDescription);
        }
      },
      error: (err) => {
        this.notify.error("Erro ao atualizar idioma");
      },
    });
  }

  toggleRightSidebar() {
    this.settingsButtonClicked.emit();
  }

  toggleMobileMenu(event: any) {
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  logout() {
    this.authService.logout();
    this.router.navigate(["/account/login"]);
  }

  fullscreen() {
    document.body.classList.toggle("fullscreen-enable");
    if (
      !document.fullscreenElement &&
      !this.element.mozFullScreenElement &&
      !this.element.webkitFullscreenElement
    ) {
      if (this.element.requestFullscreen) {
        this.element.requestFullscreen();
      } else if (this.element.mozRequestFullScreen) {
        this.element.mozRequestFullScreen();
      } else if (this.element.webkitRequestFullscreen) {
        this.element.webkitRequestFullscreen();
      } else if (this.element.msRequestFullscreen) {
        this.element.msRequestFullscreen();
      }
    } else {
      if (this.document.exitFullscreen) {
        this.document.exitFullscreen();
      } else if (this.document.mozCancelFullScreen) {
        this.document.mozCancelFullScreen();
      } else if (this.document.webkitExitFullscreen) {
        this.document.webkitExitFullscreen();
      } else if (this.document.msExitFullscreen) {
        this.document.msExitFullscreen();
      }
    }
  }
}
